import React, { useEffect, useMemo, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import EditOutlined from '@material-ui/icons/EditOutlined'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
    deleteReport,
    fetchReports,
    fetchTenants,
    fetchTenantUsers,
    ReportModel,
    TenantUserInfo,
} from '../../redux/actions/dashboard'
import { Tenant } from '../../shared/types/tenant'
import { config } from '../../config'
import TenantUsersTable from './TenantUsersTable'
import ReportDialog from './ReportDialog'

/*
 * Prehlad tenantov pre superadmina (dashboard).
 * Kazdy tenant = jeden akordeon, v nom pod sebou:
 *   1. Tenant users - loginy tenanta, moznost nastavit heslo
 *   2. Reports      - reporty tenanta, moznost pridat / upravit / zmazat
 */

const NO_TENANT_KEY = '__none__'

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(2),
    },
    header: {
        padding: theme.spacing(1),
    },
    list: {
        margin: theme.spacing(1),
    },
    alert: {
        margin: theme.spacing(1),
    },
    summary: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: theme.spacing(1),
    },
    tenantName: {
        fontWeight: 600,
    },
    counts: {
        marginLeft: 'auto',
        display: 'flex',
        gap: theme.spacing(1),
    },
    details: {
        display: 'block',
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    block: {
        '& + &': {
            marginTop: theme.spacing(3),
        },
    },
    blockHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 32,
        marginBottom: theme.spacing(0.5),
    },
    blockTitle: {
        fontWeight: 600,
    },
    table: {
        '& td, & th': {
            paddingTop: theme.spacing(0.75),
            paddingBottom: theme.spacing(0.75),
        },
    },
    actionsCell: {
        width: 1,
        whiteSpace: 'nowrap',
    },
    urlCell: {
        maxWidth: 320,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    empty: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
    centered: {
        padding: theme.spacing(4),
        textAlign: 'center',
    },
}))

interface Props {
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

interface TenantGroup {
    key: string
    tenant?: Tenant
    users: TenantUserInfo[]
    reports: ReportModel[]
}

const sameId = (a?: string | null, b?: string | null) => Boolean(a && b && a.toLowerCase() === b.toLowerCase())

export default function TenantOverview({ onSuccess, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const loginTenantId = useSelector((state: any) => state.login.value.user.tenantId)
    // pozor: default stav reducera je executed: true + value: null, preto sa riadime podla value
    const tenantsValue: Tenant[] | null = useSelector((state: any) => state.tenants.value)
    const tenants: Tenant[] = useMemo(() => tenantsValue ?? [], [tenantsValue])
    const tenantsError = useSelector((state: any) => state.tenants.error)
    const reports: ReportModel[] | undefined = useSelector((state: any) => state.reports.value)

    const [users, setUsers] = useState<TenantUserInfo[]>()
    const [usersError, setUsersError] = useState<string>()
    const [expanded, setExpanded] = useState<string | false>(false)

    const [reportDialog, setReportDialog] = useState<{ report?: ReportModel; tenantId?: string | null }>()
    const [reportToDelete, setReportToDelete] = useState<ReportModel>()
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (!tenantsValue || tenantsValue.length === 0) dispatch(fetchTenants())
        loadUsers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadUsers = () => {
        fetchTenantUsers()
            .then((result) => {
                setUsers(result ?? [])
                setUsersError(undefined)
            })
            .catch((e: any) => setUsersError(e?.message || 'unknown error'))
    }

    useEffect(() => {
        dispatch(fetchReports(loginTenantId))
    }, [dispatch, loginTenantId])

    const reloadReports = () => dispatch(fetchReports(loginTenantId))

    const groups: TenantGroup[] = useMemo(() => {
        // hlavny tenant (superadmin) prvy, ostatni abecedne
        const sorted = [...tenants].sort((a, b) => {
            const aMain = sameId(a.id, config.mainTenantId) ? 0 : 1
            const bMain = sameId(b.id, config.mainTenantId) ? 0 : 1
            return aMain - bMain || (a.name ?? '').localeCompare(b.name ?? '')
        })
        const result: TenantGroup[] = sorted.map((tenant) => ({
            key: tenant.id,
            tenant,
            users: (users ?? []).filter((u) => sameId(u.tenantId, tenant.id)),
            reports: (reports ?? []).filter((r) => sameId(r.tenantId, tenant.id)),
        }))
        // reporty bez tenanta (alebo s neexistujucim tenantom) na koniec
        const orphanReports = (reports ?? []).filter((r) => !sorted.some((x) => sameId(r.tenantId, x.id)))
        if (orphanReports.length > 0) {
            result.push({ key: NO_TENANT_KEY, users: [], reports: orphanReports })
        }
        return result
    }, [tenants, users, reports])

    const handleDelete = async () => {
        if (!reportToDelete?.id) return
        setDeleting(true)
        try {
            await deleteReport(reportToDelete.id)
            onSuccess(t('dashboard.reportDeleted'))
            setReportToDelete(undefined)
            reloadReports()
        } catch {
            onError(t('dashboard.errorDeletingReport'))
        } finally {
            setDeleting(false)
        }
    }

    const loading = (!tenantsValue && !tenantsError) || (!users && !usersError) || !reports

    const renderUsers = (group: TenantGroup, tenantName: string) => (
        <div className={classes.block}>
            <TenantUsersTable
                users={group.users}
                tenantId={group.tenant?.id}
                tenantName={tenantName}
                isSuperAdmin={true}
                loadError={usersError}
                onChanged={loadUsers}
                onSuccess={onSuccess}
                onError={onError}
            />
        </div>
    )

    const renderReports = (group: TenantGroup) => (
        <div className={classes.block}>
            <div className={classes.blockHeader}>
                <Typography variant="subtitle2" className={classes.blockTitle}>
                    {t('reports.title')}
                </Typography>
                <Button
                    size="small"
                    color="primary"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={() => setReportDialog({ tenantId: group.tenant?.id ?? null })}
                >
                    {t('dashboard.addReport')}
                </Button>
            </div>
            <Table size="small" className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('common.name')}</TableCell>
                        <TableCell>{t('reports.url')}</TableCell>
                        <TableCell>{t('common.description')}</TableCell>
                        <TableCell className={classes.actionsCell} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {group.reports.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={4} className={classes.empty}>
                                {t('dashboard.noReports')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        group.reports.map((report) => (
                            <TableRow key={report.id} hover>
                                <TableCell>{report.name || t('common.none')}</TableCell>
                                <TableCell className={classes.urlCell}>
                                    {report.url ? (
                                        <a href={report.url} target="_blank" rel="noopener noreferrer" title={report.url}>
                                            {report.url}
                                        </a>
                                    ) : (
                                        t('common.none')
                                    )}
                                </TableCell>
                                <TableCell>{report.description || ''}</TableCell>
                                <TableCell className={classes.actionsCell}>
                                    <Tooltip title={t('dashboard.editReport') as string}>
                                        <IconButton size="small" onClick={() => setReportDialog({ report })}>
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('dashboard.deleteReport') as string}>
                                        <IconButton size="small" onClick={() => setReportToDelete(report)}>
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    )

    const renderGroup = (group: TenantGroup) => {
        const tenantName: string = group.tenant?.name ?? String(t('dashboard.withoutTenant'))
        const isMain = sameId(group.tenant?.id, config.mainTenantId)
        return (
            <Accordion
                key={group.key}
                expanded={expanded === group.key}
                onChange={(_, open) => setExpanded(open ? group.key : false)}
                TransitionProps={{ unmountOnExit: true }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className={classes.summary}>
                        <Typography className={classes.tenantName}>{tenantName}</Typography>
                        {isMain ? <Chip size="small" color="primary" label={t('dashboard.mainTenant')} /> : null}
                        <div className={classes.counts}>
                            {group.tenant ? (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('dashboard.loginsCount', { count: group.users.length })}
                                />
                            ) : null}
                            <Chip
                                size="small"
                                variant="outlined"
                                label={t('dashboard.reportsCount', { count: group.reports.length })}
                            />
                        </div>
                    </div>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                    {group.tenant ? renderUsers(group, tenantName) : null}
                    {renderReports(group)}
                </AccordionDetails>
            </Accordion>
        )
    }

    return (
        <Grid className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h6">{t('dashboard.tenantsTitle')}</Typography>
            </div>
            {tenantsError ? (
                <Alert severity="error" className={classes.alert}>
                    {t('dashboard.errorLoadingTenants')}
                </Alert>
            ) : null}
            {usersError ? (
                <Alert severity="error" className={classes.alert}>
                    {t('dashboard.errorLoadingTenantUsers')} ({usersError})
                </Alert>
            ) : null}
            <div className={classes.list}>
                {loading ? (
                    <div className={classes.centered}>
                        <CircularProgress size={40} />
                    </div>
                ) : (
                    groups.map(renderGroup)
                )}
            </div>

            <ReportDialog
                open={Boolean(reportDialog)}
                report={reportDialog?.report}
                defaultTenantId={reportDialog?.tenantId}
                tenants={tenants}
                onClose={() => setReportDialog(undefined)}
                onSaved={() => {
                    setReportDialog(undefined)
                    onSuccess(t('dashboard.reportSaved'))
                    reloadReports()
                }}
                onError={onError}
            />

            <Dialog open={Boolean(reportToDelete)} onClose={() => !deleting && setReportToDelete(undefined)}>
                <DialogTitle>{t('dashboard.deleteReport')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('dashboard.deleteReportConfirm', { name: reportToDelete?.name })}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReportToDelete(undefined)} disabled={deleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
